/**
 * WelcomeController is a class that manages the logic for the welcome screen of the application.
 * It handles user authentication, navigation, and various UI interactions.
 * 
 * @class
 * @param {Object} $scope - AngularJS scope object.
 * @param {Object} $location - AngularJS $location service for navigation.
 * @param {Object} $injector - AngularJS $injector service for dependency injection.
 * @param {Object} TaskInfoService - Service to retrieve task-related information.
 * @param {Object} UserInfoService - Service to retrieve user-related information.
 * @param {Object} BridgeRuntimeService - Service to manage runtime operations.
 * @param {Object} $signals - AngularJS $signals service for event handling.
 * @param {Object} $timeout - AngularJS $timeout service for delayed function execution.
 * @param {Object} $rootScope - AngularJS $rootScope for global state management.
 * @param {Object} HybridTimeService - Service to manage hybrid time-related operations.
 */
export class WelcomeController {
    constructor($scope, $location, $injector, TaskInfoService, UserInfoService, BridgeRuntimeService, $signals, $timeout, $rootScope, HybridTimeService) {
        // Controller meta setup
        $scope._logIdentifier = 'WelcomeController';
        $scope.showSecondaryAccessCodeInput = false;

        this.$scope = $scope;
        this.$location = $location;
        this.$injector = $injector;
        this.TaskInfoService = TaskInfoService;
        this.UserInfoService = UserInfoService;
        this.BridgeRuntimeService = BridgeRuntimeService;
        this.$signals = $signals;
        this.$timeout = $timeout;
        this.$rootScope = $rootScope;
        this.HybridTimeService = HybridTimeService;

        // Lazy-loaded service to avoid circular dependencies
        let _AuthenticationBridgeService;
        this.AuthenticationBridgeService = function () {
            if (!_AuthenticationBridgeService) {
                _AuthenticationBridgeService = $injector.get('AuthenticationBridgeService');
            }
            return _AuthenticationBridgeService;
        };

        // For DEV MODE: If user is already logged in, push to overview page
        // This is to ensure that user doesn't have to restart if accidentially pushed to "#"
        if (this.AuthenticationBridgeService().isUnlocked()) {
            const candidate = this.UserInfoService.getUserInfo();
            if (typeof candidate !== 'undefined' && candidate !== null) {
                this.$location.path('/overview/' + candidate.getTaskUUID());
                return;
            }
        }

        // Controller attributes
        $scope.taskInfo = this.TaskInfoService.getTaskInfo();

        // Redirect to VM detection if within the timeframe
        if ($scope.taskInfo.isInTimeframe()) {
            this.$location.path("/vm-detection");
        }

        // Bind methods to the controller instance
        $scope.onButtonQuitClick = this.onButtonQuitClick.bind(this);
        $scope.showSecondaryAccessCode = this.showSecondaryAccessCode.bind(this);
        $scope.validateAccessCode = this.validateAccessCode.bind(this);
        $scope.showInvigilatorLogin = this.showInvigilatorLogin.bind(this);
        $scope.invigilatorLogin = this.invigilatorLogin.bind(this);

        // Initialize media assessment flag
        $scope.isMediaAssessment = false;
        if ($scope.taskInfo.isMediaAssessment()) {
            $scope.isMediaAssessment = true;
        }
    }

    /**
     * Handles the click event for the quit button.
     * 
     * @method
     */
    onButtonQuitClick() {
        this.BridgeRuntimeService.quit(false);
    }

    /**
     * Determines if the secondary access code input should be shown.
     * 
     * @method
     * @returns {boolean} - True if the secondary access code input should be shown, otherwise false.
     */
    showSecondaryAccessCode() {
        return (this.$scope.taskInfo.getPackageType() === 'school' && this.$scope.taskInfo.isSecondaryAccessExpired());
    }

    /**
     * Validates the provided access code.
     * 
     * @method
     * @param {string} code - The access code to validate.
     */
    validateAccessCode(code) {
        if (code && angular.sha256(code) === this.$scope.taskInfo.getSecondaryAccessCode()) {
            this.$scope.secondaryAccessCodeError = false;
            this.HybridTimeService.activateSecondaryAccessCode();
            this.$location.path('/login');
        } else {
            this.$scope.secondaryAccessCodeError = true;
        }
    }

    /**
     * Determines if the invigilator login option should be shown.
     * 
     * @method
     * @returns {boolean} - True if the invigilator login option should be shown, otherwise false.
     */
    showInvigilatorLogin() {
        return this.$scope.taskInfo.isInvigilatorLoginAccessable();
    }

    /**
     * Handles the invigilator login process.
     * 
     * @method
     */
    invigilatorLogin() {
        this.$rootScope.invigilatorLogin = true;
        this.$location.path('/login');
    }
}

export default function registerWelcomeController(assessmentFrontendControllers) {
    assessmentFrontendControllers
    .controller('WelcomeController', 
        [
            '$scope', '$location', '$injector', 'TaskInfoService', 'UserInfoService', 'BridgeRuntimeService', '$signals', '$timeout', '$rootScope', 'HybridTimeService', 
            WelcomeController
        ]);
}